import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/common/Button';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { PasswordStrength, usePasswordValidation } from '@/components/auth/PasswordStrength';
import { CUERPOS_OPOSICION, getCuerpoById } from '@/data/cuerposOposicion';

export function RegisterOpositorPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [opositorBody, setOpositorBody] = useState('secretaria');
  const [autonomousCommunity, setAutonomousCommunity] = useState('extremadura');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signUpWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { isValid: isPasswordValid } = usePasswordValidation(password);

  const queryParams = new URLSearchParams(window.location.search);
  const sessionId = queryParams.get('session_id');

  const cuerpoActual = getCuerpoById(opositorBody);
  const requiresCCAA = cuerpoActual?.requiresCCAA ?? false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPasswordValid) return toast.error('La contraseña no cumple los requisitos');
    if (!termsAccepted) return toast.error('Debes aceptar los términos');
    if (cuerpoActual && !cuerpoActual.available) {
      return toast.error('Ese cuerpo está próximamente disponible. Elige otro o suscríbete a notificaciones.');
    }
    setIsLoading(true);
    try {
      const { error } = await signUpWithEmail(
        email.trim().toLowerCase(),
        password,
        fullName,
        'opositor',
        undefined,
        requiresCCAA ? autonomousCommunity : undefined,
        opositorBody,
        sessionId || undefined
      );
      if (error) toast.error(error.message || 'Error al registrar');
      else navigate('/auth/opositores/pending-confirmation', { state: { email, userType: 'opositor' } });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    localStorage.setItem('munilex_auth_intent', 'opositor');
    localStorage.setItem('munilex_auth_opositor_body', opositorBody);
    const { error } = await signInWithGoogle();
    if (error) toast.error(error.message || 'Error con Google');
  };

  const goldBtn = 'w-full h-11 rounded-lg font-semibold bg-[hsl(45,85%,55%)] hover:bg-[hsl(45,85%,48%)] text-[hsl(38,80%,15%)] transition-colors disabled:opacity-60';

  return (
    <div>
      <Link to="/auth/opositores/login" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-3 w-3" /> Volver al login
      </Link>
      {sessionId && (
        <div className="mb-6 p-4 rounded-xl border-2 border-green-500/20 bg-green-500/5 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-500">
          <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center shrink-0">
            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-bold text-green-700 dark:text-green-400">¡Pago verificado con éxito!</p>
            <p className="text-[11px] text-green-600/80 dark:text-green-400/60 leading-tight">Completa tu registro para activar tu suscripción Premium inmediatamente.</p>
          </div>
        </div>
      )}
      <h2 className="text-2xl font-bold mb-1">Crear cuenta — Opositor</h2>
      <p className="text-sm text-muted-foreground mb-5">Tests adaptativos, casos prácticos y tutoría 24/7</p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-semibold mb-1.5">Nombre completo</label>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} required
            className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-[hsl(45,85%,55%)]" />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1.5">Correo electrónico</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-[hsl(45,85%,55%)]" />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-1.5">Contraseña</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-[hsl(45,85%,55%)]" />
          <PasswordStrength password={password} accentClass="text-[hsl(38,75%,38%)]" />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">¿Qué oposición preparas?</label>
          <div className="grid grid-cols-2 gap-2">
            {CUERPOS_OPOSICION.filter(c => c.id !== 'generico').map((c) => (
              <button key={c.id} type="button" onClick={() => setOpositorBody(c.id)}
                className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                  opositorBody === c.id
                    ? 'border-[hsl(45,85%,55%)] bg-[hsl(45,85%,55%)]/10'
                    : 'border-border hover:border-[hsl(45,85%,55%)]/50'
                }`}>
                {!c.available && (
                  <span className="absolute top-1 right-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wider">
                    Pronto
                  </span>
                )}
                <div className="text-xl mb-1">{c.icon}</div>
                <div className="font-semibold text-sm leading-tight">{c.shortLabel}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{c.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">O también puedes estudiar solo la base común</label>
          <div className="grid grid-cols-1 gap-2">
            {CUERPOS_OPOSICION.filter(c => c.id === 'generico').map((c) => (
              <button key={c.id} type="button" onClick={() => setOpositorBody(c.id)}
                className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                  opositorBody === c.id
                    ? 'border-amber-500 bg-amber-500/10'
                    : 'border-border hover:border-amber-500/50'
                }`}>
                <div className="text-xl mb-1">{c.icon}</div>
                <div className="font-semibold text-sm leading-tight">{c.shortLabel}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{c.description}</div>
              </button>
            ))}
          </div>
        </div>

        {requiresCCAA && (
          <div>
            <label className="block text-sm font-semibold mb-2">Comunidad Autónoma</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'extremadura', label: 'Extremadura' },
                { id: 'andalucia', label: 'Andalucía' },
              ].map((c) => (
                <button key={c.id} type="button" onClick={() => setAutonomousCommunity(c.id)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    autonomousCommunity === c.id
                      ? 'border-[hsl(45,85%,55%)] bg-[hsl(45,85%,55%)]/10'
                      : 'border-border hover:border-[hsl(45,85%,55%)]/50'
                  }`}>
                  <div className="font-semibold text-sm">{c.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 pt-1">
          <input id="terms-op" type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border text-[hsl(45,85%,55%)] focus:ring-2 focus:ring-[hsl(45,85%,55%)]" />
          <label htmlFor="terms-op" className="text-sm text-muted-foreground">
            Acepto los <a href="/terms" className="text-[hsl(38,75%,38%)] hover:underline">Términos</a> y la{' '}
            <a href="/privacy" className="text-[hsl(38,75%,38%)] hover:underline">Política de Privacidad</a>
          </label>
        </div>

        <button type="submit" className={goldBtn} disabled={isLoading || !isPasswordValid || !termsAccepted}>
          {isLoading ? 'Creando cuenta...' : 'Crear cuenta'}
        </button>
      </form>

      <div className="my-6 flex items-center gap-4">
        <div className="flex-1 h-px bg-border" />
        <span className="text-sm text-muted-foreground">o</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <Button type="button" variant="outline" className="w-full" onClick={handleGoogleSignup}>
        Continuar con Google
      </Button>

      <p className="text-sm text-center text-muted-foreground mt-6">
        ¿Ya tienes cuenta?{' '}
        <Link to="/auth/opositores/login" className="text-[hsl(38,75%,38%)] hover:underline font-medium">Inicia sesión</Link>
      </p>
    </div>
  );
}
